import React, { useCallback, useEffect, useRef, useState } from 'react';

// Anti-spam for the public website forms that create leads (Admin > Leads):
// - a hidden "website" field people never see; bots that fill every input give themselves away;
// - Cloudflare Turnstile, shown only when REACT_APP_TURNSTILE_SITE_KEY is set at build time
//   (the backend checks the token when TURNSTILE_SECRET_KEY is set).
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise;
const loadTurnstile = () => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = SCRIPT;
      s.async = true;
      s.defer = true;
      s.onload = () => resolve(window.turnstile);
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
};

/** Returns { guard, guardFields(), resetGuard() }: render `guard` inside the form, spread guardFields() into the body. */
export const useLeadGuard = () => {
  const honeypot = useRef(null);
  const box = useRef(null);
  const widget = useRef(null);
  const [token, setToken] = useState('');

  useEffect(() => {
    if (!SITE_KEY || !box.current) return undefined;
    let cancelled = false;
    loadTurnstile().then((ts) => {
      if (cancelled || !ts || !box.current || widget.current !== null) return;
      widget.current = ts.render(box.current, {
        sitekey: SITE_KEY,
        callback: setToken,
        'expired-callback': () => setToken(''),
        'error-callback': () => setToken(''),
      });
    }).catch(() => { /* blocked by the browser: the backend still accepts the lead if it can't verify */ });
    return () => {
      cancelled = true;
      if (widget.current !== null && window.turnstile) window.turnstile.remove(widget.current);
      widget.current = null;
    };
  }, []);

  const guardFields = useCallback(() => ({
    website: honeypot.current ? honeypot.current.value : '',
    turnstile_token: token || undefined,
    page: typeof window !== 'undefined' ? window.location.pathname : undefined,
  }), [token]);

  const resetGuard = useCallback(() => {
    setToken('');
    if (widget.current !== null && window.turnstile) window.turnstile.reset(widget.current);
  }, []);

  const guard = (
    <>
      <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}>
        <label>Website <input ref={honeypot} type="text" name="website" tabIndex={-1} autoComplete="off" defaultValue="" /></label>
      </div>
      {SITE_KEY && <div ref={box} className="min-h-[65px]" />}
    </>
  );

  return { guard, guardFields, resetGuard, ready: !SITE_KEY || !!token };
};

export default useLeadGuard;
